/**
 * PostHog on the storefront — session recording plus the Vault_shop funnel events; the purchase itself is captured server-side by the order webhook, so no client purchase event exists here.
 */

export const POSTHOG_KEY = "phc_vtVb7NtbIvtfdfcJEzLGtxgfG7fMDS4fv5Yugj8HfWn";
// The first-party proxy at posthog.threadbeast.com 403s any referer off its allowlist and vault.threadbeast.com is not on it, so this stays the direct cloud host until that allowlist gains the store.
export const POSTHOG_HOST = "https://us.i.posthog.com";

/** The bundle product's handle, read via the server-rendered .tb-bundle-page marker, or null anywhere else. */
export function bundleHandle(loc = location, doc = document) {
  if (!doc.body || !doc.body.classList.contains("tb-bundle-page")) return null;
  const m = /\/products\/([^/?#]+)/.exec(loc.pathname);
  return m ? m[1] : null;
}

/** The items a /cart/add response carries — one bare item or an {items:[...]} batch. */
export function addedItems(payload) {
  const items =
    payload && Array.isArray(payload.items) ? payload.items : [payload];
  return items.filter((it) => it && (it.variant_id || it.id));
}

/** Whether a request is a cart add — a POST whose path is /cart/add or /cart/add.js in any of the theme's and the app's spellings; the method gate and the path-only match keep foreign URLs that merely carry the string from minting phantom adds. */
export function isCartAdd(url, method) {
  if (!/^post$/i.test(String(method || ""))) return false;
  return /\/cart\/add(\.js)?$/.test(String(url || "").split(/[?#]/)[0]);
}

function track(event, props, options) {
  const ph = window.posthog;
  if (ph && typeof ph.capture === "function") ph.capture(event, props, options);
}

function trackAdd(payload) {
  for (const it of addedItems(payload)) {
    track("Vault_shop.cart.item.added", {
      variant_id: it.variant_id || it.id,
      sku: it.sku,
      product_title: it.product_title || it.title,
      quantity: it.quantity,
      price_cents: it.price,
      bundle_handle: bundleHandle(),
    });
  }
}

/**
 * Cart adds are watched at the network layer — a successful /cart/add response is the real signal, and it covers the bundle app, the cart drawer and the native product form alike.
 */
function watchCartAdds() {
  const origFetch = window.fetch;
  if (origFetch) {
    window.fetch = function (input, init) {
      const p = origFetch.apply(this, arguments);
      // String(input) covers URL instances, which carry href rather than url.
      const url =
        typeof input === "string"
          ? input
          : input && input.url
            ? input.url
            : String(input ?? "");
      const method =
        (init && init.method) || (input && input.method) || "GET";
      if (isCartAdd(url, method))
        p.then((res) => {
          if (res.ok)
            res
              .clone()
              .json()
              .then(trackAdd)
              .catch(() => {});
        }).catch(() => {});
      return p;
    };
  }
  // XHR is wrapped too because the app's SDK is a black box about its transport.
  const origOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.__tbCartAdd = isCartAdd(url, method);
    return origOpen.apply(this, arguments);
  };
  const origSend = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.send = function () {
    if (this.__tbCartAdd)
      // once, because a reused XHR instance calls send again and a second listener would double-count.
      this.addEventListener(
        "load",
        () => {
          if (this.status >= 200 && this.status < 300) {
            try {
              trackAdd(JSON.parse(this.responseText));
            } catch {
              /* not JSON — nothing to track */
            }
          }
        },
        { once: true },
      );
    return origSend.apply(this, arguments);
  };
}

/**
 * Pick-by-pick progress read from the app's slot containers on mutations, because the app replaces the footer on every pick — the same pattern as tb-cart-button.js.
 */
function watchSlots() {
  let last = 0;
  const read = () => {
    const slots = document.querySelectorAll(
      ".gbbBundleFooter .gbbProductSlotContainer",
    );
    if (!slots.length) return;
    let filled = 0;
    for (const slot of slots) if (slot.dataset.productId) filled++;
    // Only upward changes fire: a fill is a funnel step, an un-pick is not.
    if (filled > last)
      track("Vault_shop.bundle_page.item_slot.filled", {
        filled,
        required: slots.length,
        bundle_handle: bundleHandle(),
      });
    last = filled;
  };
  new MutationObserver(read).observe(document.body, {
    childList: true,
    subtree: true,
  });
}

const CHECKOUT_SELECTOR =
  'a[href*="/checkout"], button[name="checkout"], input[name="checkout"]';

/**
 * sendBeacon because the click navigates off the page, and a debounce because one tap can bubble through several matching ancestors; the bundle app's auto-checkout navigates without a click, so on that path the cart add above is the last client event before Shopify's checkout takes over.
 */
function watchCheckout() {
  let lastAt = 0;
  const fire = () => {
    const now = Date.now();
    if (now - lastAt < 1000) return;
    lastAt = now;
    track(
      "Vault_shop.cart.checkout_button.clicked",
      { bundle_handle: bundleHandle() },
      { transport: "sendBeacon" },
    );
  };
  document.addEventListener(
    "click",
    (e) => {
      const el =
        e.target && e.target.closest && e.target.closest(CHECKOUT_SELECTOR);
      if (el) fire();
    },
    true,
  );
  document.addEventListener(
    "submit",
    (e) => {
      const action =
        e.target && e.target.getAttribute
          ? e.target.getAttribute("action") || ""
          : "";
      if (action.includes("/checkout")) fire();
    },
    true,
  );
}

function loadPostHog() {
  // The posthog-js loader snippet with the script src built from api_host: it stubs window.posthog so captures queue until array.js arrives.
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.crossOrigin = "anonymous"),
          (p.async = !0),
          (p.src = s.api_host + "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(
            p,
            r,
          );
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return (
                "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e
              );
            },
            u.people.toString = function () {
              return u.toString(1) + ".people (stub)";
            },
            o =
              "init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug getPageViewId captureTraceFeedback captureTraceMetric".split(
                " ",
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);

  // Session recording is switched on by the project's remote config, not a flag here; the init options mirror the main site's.
  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: "https://us.posthog.com",
    defaults: "2025-05-24",
    person_profiles: "identified_only",
  });
}

export function install() {
  // The theme editor's preview is not a shopper.
  if (window.Shopify && window.Shopify.designMode) return;
  loadPostHog();
  watchCartAdds();
  watchCheckout();
  const handle = bundleHandle();
  if (handle) {
    track("Vault_shop.bundle_page.session_start", { bundle_handle: handle });
    watchSlots();
  }
}

if (typeof document !== "undefined") {
  const start = () => install();
  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", start, { once: true });
  else start();
}
